<script lang="ts">
  /**
   * CategoryBadge
   *
   * Shared category pill used across Boards/Posts listing & show pages.
   * Encapsulates the dynamic `color-mix()` inline style so consumers
   * don't repeat the same `style="background-color: color-mix(...)"` snippet.
   *
   * Variants:
   *   - 'badge'  (default): DaisyUI-flavored .badge — used in Posts/Boards Show
   *                         and Posts Index card lists.
   *   - 'tag'             : Hashtag-style pill (`#name`) used in Boards Index.
   *                         Matches the pre-existing `px-2.5 py-1 rounded-lg`
   *                         look-and-feel for hashtag clouds.
   *
   * Props:
   *   - category : { color: string; name: string }
   *   - variant  : 'badge' | 'tag'  (default: 'badge')
   *   - size     : 'sm' | 'md'      (default: 'sm')
   *
   * Color rendering:
   *   - badge variant: background = color-mix(in srgb, <color> 15%, transparent)
   *   - tag   variant: background = color-mix(in srgb, <color>  8%, transparent)
   *                    (preserves visual parity with the previous
   *                    `{color.hex}15` 8-bit-alpha suffix used at the call
   *                    site, which evaluates to 0x15/0xFF ≈ 8.2% opacity)
   *   Foreground in both variants = <color>.
   */

  interface Category {
    color: string;
    name: string;
  }

  export let category: Category;
  export let variant: 'badge' | 'tag' = 'badge';
  export let size: 'sm' | 'md' = 'sm';

  // Class composition: keep the exact same classes the call sites had.
  $: classes =
    variant === 'tag'
      ? `px-2.5 py-1 text-xs font-medium rounded-lg transition-colors hover:opacity-80`
      : `badge ${size === 'md' ? '' : 'text-xs font-medium'}`.trim();

  $: mixPct = variant === 'tag' ? 8 : 15;
  $: style = `background-color: color-mix(in srgb, ${category.color} ${mixPct}%, transparent); color: ${category.color}`;
</script>

<span class={classes} {style}>
  {#if variant === 'tag'}#{/if}{category.name}
</span>
